import React, { useEffect, useMemo, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Box, CircularProgress, Link as MuiLink } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import theme from 'theme'
import { mapMessagesForDownload } from 'lib/messagesLib'

import ConfirmBar from 'components/atoms/ConfirmBar'
import SearchField from 'components/atoms/SearchField'
import CarsList from 'components/atoms/CarsList'
import CarFiltersColumn from 'components/atoms/CarsFiltersColumn'
import CarCard from 'components/atoms/CarCard'
import AppealingIndex from 'components/atoms/AppealingIndex'
import DownloadBar from 'components/atoms/DownloadBar'
import LogIcon from 'components/atoms/LogIcon'

import useCarsFilters from 'hooks/useCarsFilters'
import useLogs from 'hooks/useLogs'
import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useInputOutput from 'hooks/useInputOutput'

import {
  getCarFinalPrice,
  getAssignmentChoiceTotalsByPlate,
  getAssignmentState,
  getAutoAssignmentDisplayState,
} from '../Assignment/assignmentLib'
import AssignmentCarAdditionalDetails from '../Assignment/AssignmentCarAdditionalDetails'
import AssignmentCarDetails from '../AssignmentCarDetails'
import AssignmentConfirmationDialog from '../Assignment/AssignmentConfirmationDialog'

import AssignmentChoicesRecap from './AssignmentChoicesRecap'

const listIncludesPartner = (list = [], partnerId) =>
  Array.isArray(list)
    ? list.includes(partnerId)
    : typeof list === 'string'
    ? list.includes(partnerId)
    : false

const isPresentValue = (value) =>
  value !== null && value !== undefined && value !== ''

const getPartnerBlindOfferValue = (car, partnerId, fallbackValue) => {
  const candidateLists = [
    car?.partners,
    car?.choices,
    car?.partnerChoices,
    car?.offers,
  ].filter(Array.isArray)

  for (const list of candidateLists) {
    const partnerChoice = list.find((item) => {
      const itemPartnerId =
        item?.partnerId ||
        item?.id ||
        item?.partner?.partnerId ||
        item?.partner?.id
      return itemPartnerId === partnerId
    })

    if (!partnerChoice) {
      continue
    }

    const blindOfferValue =
      partnerChoice?.blindPrice ??
      partnerChoice?.blindFinalPrice ??
      partnerChoice?.blindOfferPrice ??
      partnerChoice?.blind_price ??
      partnerChoice?.blindOffer ??
      partnerChoice?.blindValue ??
      partnerChoice?.bidPrice ??
      partnerChoice?.offerPrice ??
      partnerChoice?.reprice

    if (isPresentValue(blindOfferValue)) {
      return blindOfferValue
    }
  }

  return fallbackValue
}

function getRows(
  titlesRow,
  cars,
  t,
  session,
  partners,
  assignmentChoiceTotalsByPlate = {}
) {
  return [
    titlesRow,
    ...cars.map((car) => {
      const autoAssignmentDisplayState = getAutoAssignmentDisplayState(car)
      const hasFinalColumn =
        session.agenda.assignment.state === 'closed' ||
        car?.autoAssignmentPartnerId !== car?.assignedPartnerId

      const finalAssignment = hasFinalColumn
        ? t(
            `assignment.excel.status.${
              session.agenda.assignment.state
            }.${getAssignmentState(car)}`
          )
        : '-'
      const finalPrice = getCarFinalPrice(car)
      const isBlindPlus = autoAssignmentDisplayState === 'blind plus'
      const defaultBlindOfferValue = finalPrice ?? '-'
      const firstChoiceCount =
        Number(assignmentChoiceTotalsByPlate?.[car.plate]?.firstChoice) || 0
      const blindOffersCount =
        Number(assignmentChoiceTotalsByPlate?.[car.plate]?.blindOffers) || 0

      return [
        car.plate,
        `${car.title}`,
        t(
          `assignment.excel.status.${session.agenda.assignment.state}.${autoAssignmentDisplayState}`
        ),
        finalAssignment,
        finalPrice ?? '-',
        (car.autoAssignmentPartnerId &&
          partners.find(
            (partner) => partner.partnerId === car.autoAssignmentPartnerId
          )?.name) ||
          '-',
        (car.assignedPartnerId &&
          hasFinalColumn &&
          partners.find(
            (partner) => partner.partnerId === car.assignedPartnerId
          )?.name) ||
          '-',
        `${firstChoiceCount}`,
        `${blindOffersCount}`,
        ...partners.map((partner) =>
          listIncludesPartner(car.firstChoiceList, partner.partnerId)
            ? isBlindPlus
              ? getPartnerBlindOfferValue(
                  car,
                  partner.partnerId,
                  defaultBlindOfferValue
                )
              : '1'
            : listIncludesPartner(car.secondChoiceList, partner.partnerId)
            ? '2'
            : '-'
        ),
        car.messages ? mapMessagesForDownload(car.messages, t, true) : '-',
      ]
    }),
  ]
}

export default function AssignmentCarAssignment({
  session,
  partners,
  cars,
  handleValidation,
  handleConfirm,
  dateValue,
  setDateValue,
  readOnly,
  contentIsReadOnly,
  refetchList,
  setShowHeader,
}) {
  const { t } = useTranslation()

  const { saveLogs, isFetching: isFetchingLogs } = useLogs()

  const [showDetails, setShowDetails] = useState()
  const [filteredCars, setFilteredCars] = useState([])

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(handleConfirm)

  useEffect(() => {
    setShowHeader(!showDetails)
  }, [setShowHeader, showDetails])

  const {
    filters,
    filtersData,
    initFilters,
    setKmRange,
    setDamageRange,
    setYearRange,
    setBrands,
    setModels,
    setLocations,
    setFuels,
    setCategory,
    setQuery,
    setAssignmentCaps: setFilteringCaps,
    setAssignmentType,
    setAssignmentPartners,
    filterCars,
    resetFilters,
  } = useCarsFilters({
    startDataFilters: {
      assignmentPartners: [
        { label: t('common.form.all'), value: '' },
        ...partners.map((partner) => ({
          label: partner.name,
          value: partner.partnerId,
        })),
      ],
    },
  })

  const { saveXls } = useInputOutput()

  useEffect(() => {
    if (!cars.length) {
      return
    }

    initFilters(cars)
  }, [cars, initFilters])

  const handleSetFilteredCars = useDebouncedCallback(
    (cars, filters, setFilteredCars, filterCars) =>
      setFilteredCars(filterCars(cars, filters)),
    100
  )

  useEffect(() => {
    handleSetFilteredCars(cars, filters, setFilteredCars, filterCars)
  }, [cars, handleSetFilteredCars, filters, setFilteredCars, filterCars])

  const assignmentChoiceTotalsByPlate = useMemo(
    () => getAssignmentChoiceTotalsByPlate(cars),
    [cars]
  )

  const enrichedFilteredCars = useMemo(
    () =>
      filteredCars.map((car) => {
        const totals = assignmentChoiceTotalsByPlate[car.plate] || {}
        return {
          ...car,
          firstChoicePartnersCount: totals.firstChoice ?? 0,
          blindOfferPartnersCount: totals.blindOffers ?? 0,
        }
      }),
    [assignmentChoiceTotalsByPlate, filteredCars]
  )

  const handleDownload = async () => {
    const titlesRow = [
      t('common.excel.plate'),
      t('common.excel.name'),
      t('assignment.excel.originalStatus'),
      t(`assignment.excel.finalStatus.${session.agenda.assignment.state}`),
      t('common.excel.finalPrice'),
      t('assignment.excel.originalPartner'),
      t(`assignment.excel.finalPartner.${session.agenda.assignment.state}`),
      t('common.excel.firstChoice'),
      t('common.excel.blindOffers'),
      ...partners.map((partner) => partner.name),
      t('common.excel.messages'),
    ]

    const rows = getRows(
      titlesRow,
      cars,
      t,
      session,
      partners,
      assignmentChoiceTotalsByPlate
    )

    saveXls('car-assignment', rows)
  }

  const handleOpenDetails = (car) =>
    setShowDetails({
      plate: car.plate,
      assignmentType: car.assignmentType || 'nonBlind',
    })

  return showDetails ? (
    <AssignmentCarDetails
      plate={showDetails?.plate}
      assignmentType={showDetails?.assignmentType}
      session={session}
      cars={enrichedFilteredCars}
      setShowDetails={setShowDetails}
      refetchList={refetchList}
      contentIsReadOnly={contentIsReadOnly}
    />
  ) : (
    <>
      <Box mt={1} data-testid="assignment-car" />
      <ConfirmBar
        label={t('assignment.car-assignment.confirmButton')}
        left={
          <SearchField
            data-testid="text-search"
            placeholder={t('common.form.searchBy')}
            value={filters.query}
            onChange={(event) => setQuery(event.target.value)}
          />
        }
        leftProps={{ sx: { width: 0.5 } }}
        right={
          <>
            <Collapse orientation="horizontal" in={isFetchingLogs}>
              <div style={{ marginLeft: 4, marginRight: 4 }}>
                <CircularProgress size={20} />
              </div>
            </Collapse>
            <LogIcon
              disabled={isFetchingLogs}
              handleClick={() =>
                saveLogs(`logs_assignment_${session?.sessionId}`, {
                  area: t('common.logs.assignment'),
                  sessionId: session?.sessionId,
                })
              }
            />
            <DownloadBar handleDownload={handleDownload} />
          </>
        }
        top={
          <AssignmentChoicesRecap
            cars={cars}
            filters={filters}
            filtersData={filtersData}
            setAssignmentPartners={setAssignmentPartners}
            setFilteringCaps={setFilteringCaps}
            setAssignmentType={setAssignmentType}
          />
        }
        topProps={{
          sx: {
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          },
        }}
        disabled={
          !handleValidation() ||
          !!session?.assignmentEndedAt ||
          readOnly ||
          contentIsReadOnly
        }
        handleConfirm={handleOpenConfirmationDialog}
        sticky
      />
      <CarsList
        left={
          <CarFiltersColumn
            filters={filters}
            filtersData={filtersData}
            filteredCars={
              filteredCars?.length !== cars.length ? filteredCars : undefined
            }
            setKmRange={setKmRange}
            setDamageRange={setDamageRange}
            setYearRange={setYearRange}
            setBrands={setBrands}
            setModels={setModels}
            setLocations={setLocations}
            setFuels={setFuels}
            setCategory={setCategory}
            resetFilters={resetFilters}
            readOnly={readOnly}
          />
        }
        right={
          <>
            {enrichedFilteredCars.map((car, index) => (
              <CarCard
                data-testid={`car-${index}`}
                key={`${car.assignmentType || 'nonBlind'}-${car.plate}`}
                onClick={() => handleOpenDetails(car)}
                height={theme.custom.carCardAdditionalMaxHeight}
                car={car}
                right={
                  <>
                    <AppealingIndex value={car.appealing} />
                    <MuiLink
                      color="primary"
                      data-testid="car-detail-right-click"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleOpenDetails(car)}
                      component={Box}
                      variant="body2"
                    >
                      <strong>{t('carsToBuy.details')}</strong>
                    </MuiLink>
                  </>
                }
                bottom={
                  <AssignmentCarAdditionalDetails
                    partners={partners.map((partner) => ({
                      partnerId: partner.partnerId,
                      partnerName: partner.name,
                    }))}
                    car={car}
                    sessionState={session.agenda.assignment.state}
                  />
                }
              />
            ))}
          </>
        }
      />
      <AssignmentConfirmationDialog
        open={openConfirmationDialog}
        handleClose={handleCloseConfirmationDialog}
        handleSubmit={handleConfirmConfirmationDialog}
        dateValue={dateValue}
        setDateValue={setDateValue}
      />
    </>
  )
}

AssignmentCarAssignment.propTypes = {
  session: T.object.isRequired,
  partners: T.array,
  cars: T.array,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func,
  dateValue: T.instanceOf(Date),
  setDateValue: T.func,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
}
